/**
 * ArcheBoardScroller — canonical layout for horizontally-scrolling "board"
 * views (Kanban-style column rows) across arches.
 *
 * The problem it solves: a raw `overflow-x-auto` board grows to the height of
 * its tallest column, which pushes the horizontal scrollbar below the fold —
 * so a user has to scroll the whole page down to reach it, then back up. This
 * component instead:
 *
 *   - fills its flex parent's height (`flex-1 min-h-0`) so the board never
 *     grows past the viewport (columns are expected to scroll vertically
 *     inside themselves),
 *   - hides the native horizontal bar (`scrollbar-hide`) and renders a styled,
 *     always-reachable scrollbar pinned at the bottom of the board region, and
 *   - maps a vertical mouse-wheel to horizontal board scroll *when the column
 *     under the cursor can't consume more vertical scroll* — so you can slide
 *     the board from anywhere over it without hijacking a tall column's own
 *     vertical scroll (the Trello/Linear/Jira behaviour).
 *
 * Generalises the Image Generator arche's local scrollbar
 * (`arche/arches/image-generator/components/ImageGeneratorScrollbar.tsx`) into
 * one reusable, dependency-free component. Knows nothing about any specific
 * arche — pass in the column row as `children`.
 *
 * Layout contract: render this inside a `flex flex-col` parent that has a
 * bounded height (e.g. a page whose root is `h-full flex flex-col`). `children`
 * should be the horizontal flex row of columns (`flex gap-4 …`).
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react';

export interface ArcheBoardScrollerProps {
  children: React.ReactNode;
  /** Extra classes for the outer wrapper. */
  className?: string;
  /** Extra classes for the inner horizontal scroll container. */
  contentClassName?: string;
  /** Accessible label for the custom scrollbar. */
  scrollbarLabel?: string;
}

export interface ScrollMetrics {
  scrollLeft: number;
  scrollWidth: number;
  clientWidth: number;
}

export interface ThumbGeometry {
  /** True when the content overflows horizontally (scrollbar should render). */
  overflow: boolean;
  /** Thumb width as a percentage of the track. */
  widthPct: number;
  /** Thumb left offset as a percentage of the track. */
  leftPct: number;
  /** 0–100 scroll progress, for `aria-valuenow`. */
  valueNow: number;
  /** Max scrollLeft (scrollWidth − clientWidth), clamped ≥ 0. */
  maxScroll: number;
}

/** Minimum thumb size so it stays grabbable on very wide boards. */
export const MIN_THUMB_WIDTH_PCT = 8;

/**
 * Pure geometry for the custom scrollbar thumb. Layout-independent so it can be
 * unit-tested without a real browser (jsdom has no layout).
 */
export function computeThumbGeometry({
  scrollLeft,
  scrollWidth,
  clientWidth,
}: ScrollMetrics): ThumbGeometry {
  const maxScroll = Math.max(0, scrollWidth - clientWidth);
  if (scrollWidth <= 0 || clientWidth <= 0 || maxScroll <= 0) {
    return { overflow: false, widthPct: 100, leftPct: 0, valueNow: 0, maxScroll: 0 };
  }
  const widthPct = Math.max(MIN_THUMB_WIDTH_PCT, (clientWidth / scrollWidth) * 100);
  const progress = Math.min(1, Math.max(0, scrollLeft / maxScroll));
  const leftPct = progress * (100 - widthPct);
  return {
    overflow: true,
    widthPct,
    leftPct,
    valueNow: Math.round(progress * 100),
    maxScroll,
  };
}

/**
 * Given a pointer x within the scrollbar track, return the scrollLeft that
 * centers the thumb under the pointer. Pure + layout-independent (all geometry
 * is passed in) so it can be unit-tested deterministically.
 */
export function scrollLeftFromPointer(params: {
  pointerX: number;
  trackLeft: number;
  trackWidth: number;
  thumbWidthPx: number;
  maxScroll: number;
}): number {
  const { pointerX, trackLeft, trackWidth, thumbWidthPx, maxScroll } = params;
  const usableTrack = Math.max(1, trackWidth - thumbWidthPx);
  const relativeX = pointerX - trackLeft - thumbWidthPx / 2;
  const clampedX = Math.min(usableTrack, Math.max(0, relativeX));
  const ratio = clampedX / usableTrack;
  return ratio * maxScroll;
}

export interface ColumnScrollState {
  scrollTop: number;
  clientHeight: number;
  scrollHeight: number;
}

/**
 * Decide whether a wheel event should scroll the board horizontally or be left
 * to the column under the cursor. Pure so the boundary logic is unit-testable.
 *
 *   - Trackpad horizontal (deltaX ≠ 0) → let the browser scroll natively.
 *   - No vertical intent (deltaY === 0) → nothing to translate.
 *   - Over a column that can still scroll further vertically in the wheel's
 *     direction → let the column scroll.
 *   - Otherwise → scroll the board horizontally.
 */
export function resolveWheelIntent(params: {
  deltaX: number;
  deltaY: number;
  column: ColumnScrollState | null;
}): 'board' | 'native' {
  const { deltaX, deltaY, column } = params;
  if (deltaX !== 0) return 'native';
  if (deltaY === 0) return 'native';
  if (column) {
    const atTop = column.scrollTop <= 0;
    const atBottom = column.scrollTop + column.clientHeight >= column.scrollHeight - 1;
    const canScroll = column.scrollHeight > column.clientHeight;
    if (canScroll && deltaY < 0 && !atTop) return 'native';
    if (canScroll && deltaY > 0 && !atBottom) return 'native';
  }
  return 'board';
}

/** Walk up from `target` to (but not including) `container`, returning the first
 * vertically-scrollable element. Used to resolve the "column under the cursor". */
function findScrollableColumn(
  target: EventTarget | null,
  container: HTMLElement,
): HTMLElement | null {
  let el = target instanceof HTMLElement ? target : null;
  while (el && el !== container) {
    // Cheap layout check first, then the (more expensive) computed-style read.
    if (el.scrollHeight > el.clientHeight) {
      const overflowY = getComputedStyle(el).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') return el;
    }
    el = el.parentElement;
  }
  return null;
}

/** Distance (px) a keyboard arrow moves the board. */
const KEYBOARD_STEP_PX = 120;
/** Thumb width fallback (px) before the real thumb has been measured. */
const FALLBACK_THUMB_WIDTH_PX = 24;
/** Approximate px per wheel "line" when a mouse reports deltaMode DOM_DELTA_LINE. */
const WHEEL_PIXELS_PER_LINE = 16;

/**
 * Normalise a wheel event's vertical delta to pixels. Firefox (and some other
 * setups) report classic mouse-wheel deltas in lines (`deltaMode === 1`) or
 * pages (`deltaMode === 2`) rather than pixels, so adding the raw `deltaY` to
 * `scrollLeft` would scroll only a few pixels per notch. Pure + unit-testable.
 */
export function normalizeWheelDeltaY(
  deltaY: number,
  deltaMode: number,
  viewportPx: number,
): number {
  if (deltaMode === 1) return deltaY * WHEEL_PIXELS_PER_LINE; // DOM_DELTA_LINE
  if (deltaMode === 2) return deltaY * viewportPx; // DOM_DELTA_PAGE
  return deltaY; // DOM_DELTA_PIXEL
}

export function ArcheBoardScroller({
  children,
  className,
  contentClassName,
  scrollbarLabel = 'Scroll board horizontally',
}: ArcheBoardScrollerProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const thumbRef = useRef<HTMLDivElement | null>(null);
  const contentId = useId();
  const [metrics, setMetrics] = useState<ScrollMetrics>({
    scrollLeft: 0,
    scrollWidth: 0,
    clientWidth: 0,
  });

  const syncMetrics = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setMetrics({
      scrollLeft: el.scrollLeft,
      scrollWidth: el.scrollWidth,
      clientWidth: el.clientWidth,
    });
  }, []);

  // Keep thumb geometry in sync with scroll position and size changes.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    syncMetrics();
    el.addEventListener('scroll', syncMetrics, { passive: true });
    const resizeObserver = new ResizeObserver(syncMetrics);
    resizeObserver.observe(el);
    // Observe the inner row too, so adding/removing columns re-measures.
    if (el.firstElementChild) resizeObserver.observe(el.firstElementChild);
    window.addEventListener('resize', syncMetrics);
    return () => {
      el.removeEventListener('scroll', syncMetrics);
      resizeObserver.disconnect();
      window.removeEventListener('resize', syncMetrics);
    };
  }, [syncMetrics]);

  // Wheel-to-horizontal. Attached natively (not via React's passive onWheel) so
  // preventDefault() actually suppresses the page/column scroll when we take over.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      const column = findScrollableColumn(e.target, el);
      const intent = resolveWheelIntent({
        deltaX: e.deltaX,
        deltaY: e.deltaY,
        column: column
          ? {
              scrollTop: column.scrollTop,
              clientHeight: column.clientHeight,
              scrollHeight: column.scrollHeight,
            }
          : null,
      });
      if (intent === 'board' && el.scrollWidth > el.clientWidth) {
        el.scrollLeft += normalizeWheelDeltaY(e.deltaY, e.deltaMode, el.clientWidth);
        e.preventDefault();
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const geometry = computeThumbGeometry(metrics);

  const scrollToPointer = useCallback((pointerX: number) => {
    const el = scrollRef.current;
    const track = trackRef.current;
    const thumb = thumbRef.current;
    if (!el || !track) return;
    const trackRect = track.getBoundingClientRect();
    el.scrollLeft = scrollLeftFromPointer({
      pointerX,
      trackLeft: trackRect.left,
      trackWidth: trackRect.width,
      thumbWidthPx: thumb?.getBoundingClientRect().width ?? FALLBACK_THUMB_WIDTH_PX,
      maxScroll: Math.max(0, el.scrollWidth - el.clientWidth),
    });
  }, []);

  const beginMouseDrag = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      scrollToPointer(e.clientX);
      const onMove = (move: MouseEvent) => scrollToPointer(move.clientX);
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [scrollToPointer],
  );

  const beginTouchDrag = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;
      scrollToPointer(touch.clientX);
      const onMove = (move: TouchEvent) => {
        const t = move.touches[0];
        if (t) scrollToPointer(t.clientX);
      };
      const onEnd = () => {
        document.removeEventListener('touchmove', onMove);
        document.removeEventListener('touchend', onEnd);
      };
      document.addEventListener('touchmove', onMove, { passive: false });
      document.addEventListener('touchend', onEnd);
    },
    [scrollToPointer],
  );

  const onThumbKeyDown = useCallback((e: React.KeyboardEvent) => {
    const el = scrollRef.current;
    if (!el) return;
    if (e.key === 'ArrowLeft') {
      el.scrollLeft -= KEYBOARD_STEP_PX;
      e.preventDefault();
    } else if (e.key === 'ArrowRight') {
      el.scrollLeft += KEYBOARD_STEP_PX;
      e.preventDefault();
    } else if (e.key === 'Home') {
      el.scrollLeft = 0;
      e.preventDefault();
    } else if (e.key === 'End') {
      el.scrollLeft = el.scrollWidth;
      e.preventDefault();
    }
  }, []);

  return (
    <div className={'flex flex-col min-h-0 ' + (className ?? '')}>
      <div
        ref={scrollRef}
        id={contentId}
        className={
          'flex-1 min-h-0 overflow-x-auto overflow-y-hidden scrollbar-hide ' +
          (contentClassName ?? '')
        }
      >
        {children}
      </div>

      {geometry.overflow && (
        <div className="flex-shrink-0 border-t border-border bg-muted/30 px-4 py-2">
          <div
            ref={trackRef}
            className="relative h-4 cursor-pointer flex items-center"
            onMouseDown={beginMouseDrag}
            onTouchStart={beginTouchDrag}
          >
            {/* Track line */}
            <div className="absolute inset-x-0 h-1.5 rounded-full bg-muted" aria-hidden="true" />
            {/* Thumb */}
            <div
              ref={thumbRef}
              role="scrollbar"
              aria-orientation="horizontal"
              aria-label={scrollbarLabel}
              aria-controls={contentId}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={geometry.valueNow}
              tabIndex={0}
              onKeyDown={onThumbKeyDown}
              className="absolute h-3 rounded-full bg-primary/70 hover:bg-primary focus-visible:bg-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring transition-colors"
              style={{ left: `${geometry.leftPct}%`, width: `${geometry.widthPct}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
